import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon, CalendarIcon, ClipboardDocumentListIcon,
  BanknotesIcon, UserCircleIcon, XMarkIcon,
  BookOpenIcon, ArrowsRightLeftIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';

const NAV = [
  { section:'Main',    name:'Dashboard',     href:'/librarian/dashboard',      icon:HomeIcon },
  { section:'Library', name:'Book Catalog',  href:'/librarian/catalog',        icon:BookOpenIcon },
  { section:'Library', name:'Issue / Return',href:'/librarian/issue-return',   icon:ArrowsRightLeftIcon },
  { section:'Library', name:'Fines',         href:'/librarian/fines',          icon:ExclamationTriangleIcon },
  { section:'Account', name:'My Attendance', href:'/librarian/own-attendance', icon:CalendarIcon },
  { section:'Account', name:'My Leaves',     href:'/librarian/own-leaves',     icon:ClipboardDocumentListIcon },
  { section:'Account', name:'My Salary',     href:'/librarian/salary',         icon:BanknotesIcon },
  { section:'Account', name:'Profile',       href:'/librarian/profile',        icon:UserCircleIcon },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .lsb { width:240px; min-width:240px; min-height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .lsb-close { display:none; }
  @media(max-width:768px){
    .lsb-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; }
    .lsb-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
    .lsb-brand { position:relative; }
  }
  .lsb-brand { padding:24px 20px 20px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; }
  .lsb-brand-icon { width:34px; height:34px; background:#f59e0b; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:10px; }
  .lsb-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .lsb-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .lsb-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .lsb-nav { flex:1; padding:12px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .lsb-nav::-webkit-scrollbar { display:none; }
  .lsb-section-label { font-size:.62rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:16px 8px 6px; }
  .lsb-link { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.82rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:2px; position:relative; }
  .lsb-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .lsb-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .lsb-link.active { background:rgba(245,158,11,.12); color:#f59e0b; }
  .lsb-link.active::before { content:''; position:absolute; left:-12px; top:25%; bottom:25%; width:3px; background:#f59e0b; border-radius:0 3px 3px 0; }
  .lsb-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .lsb-footer { padding:16px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .lsb-footer-text { font-size:.68rem; color:#353b5c; text-align:center; }
`;

const LibrarianSidebar = ({ onClose }) => {
  const location  = useLocation();
  const { brand } = useSchoolBrand();
  const sectionOrder = ['Main','Library','Account'];
  const sections = sectionOrder
    .map(sec => ({ sec, items: NAV.filter(n => n.section === sec) }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      <style>{style}</style>
      <div className="lsb">
        <div className="lsb-brand">
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="lsb-brand-icon">
                  <svg viewBox="0 0 24 24"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15Z" /></svg>
                </div>
            }
            <div>
              <div className="lsb-brand-name">{brand.name || 'School Portal'}</div>
              <div className="lsb-brand-sub">Library Panel</div>
            </div>
          </div>
          {onClose && <button className="lsb-close" onClick={onClose}><XMarkIcon /></button>}
        </div>
        <nav className="lsb-nav">
          {sections.map(({ sec, items }) => (
            <div key={sec}>
              <div className="lsb-section-label">{sec}</div>
              {items.map(item => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link key={item.name} to={item.href} className={`lsb-link${isActive ? ' active' : ''}`} onClick={onClose}>
                    <item.icon />
                    <span className="lsb-link-text">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="lsb-footer"><div className="lsb-footer-text">v1.0.0 · Library Portal</div></div>
      </div>
    </>
  );
};

export default LibrarianSidebar;
